import { AccountType, TSAccount, TSRequest } from '@pelicargo/types';
import { useCallback, useEffect, useReducer, useRef } from 'react';

import { trpc } from '../config/trpc';
import { deepMergeObjects } from '../utils/deep-merge-objects';
import { RequestSortType } from './useRequestSort';
import { useTypesense } from './useTypesense';

export type SearchRequestArgs = {
  take: number;
  skip: number;
  search: string;
  sort: RequestSortType;
  incrementPage: () => void;
  filters: {
    status?: string[];
    cargo_type?: string[];
    account_id?: number[];
    account_type?: AccountType[];
    is_known_shipper?: string; // true / false
    is_freighter_required?: string; // true / false
    request_classification?: string;
    weight_break?: number;
    payment_status?: string[];
    shipment_status?: string[];
    tenant_id?: string[];
  };
};

const arrayFilter = (field: string, values: any[]) => (values?.length > 0 ? `${field}:=[${values}]` : null);

const buildFilter = (input: SearchRequestArgs): string => {
  const filters = [
    arrayFilter('account_id', input.filters?.account_id),
    arrayFilter('account_type', input.filters?.account_type),
    arrayFilter('request_status', input.filters?.status),
    arrayFilter('cargo_type', input.filters?.cargo_type),
    arrayFilter('payment_status', input.filters?.payment_status),
    arrayFilter('shipment_status', input.filters?.shipment_status),
    arrayFilter('tenant_ids', input.filters?.tenant_id),
  ];

  if (input.filters?.is_known_shipper !== undefined && input.filters?.is_known_shipper !== null) {
    filters.push(`is_known_shipper:=${input.filters.is_known_shipper}`);
  }

  if (input.filters?.is_freighter_required !== undefined && input.filters?.is_freighter_required !== null) {
    filters.push(`is_freighter_required:=${input.filters.is_freighter_required}`);
  }

  if (input.filters?.request_classification) {
    filters.push(`classifications.name:=${input.filters?.request_classification}`);
  }
  if (input.filters?.weight_break) {
    filters.push(`weight_break:=${input.filters?.weight_break}`);
  }

  return filters.filter(Boolean).join(' && ');
};

type ExtendedRequest = TSRequest & {
  account: Omit<TSAccount, 'account_id'>;
};

type ResultState = {
  requests: ExtendedRequest[];
  dedupeCheck: Set<string>;
  hasMore: boolean;
  total: number;
  filtered: number;
};

type TSResult = {
  totalCount: number;
  filteredCount: number;
  totalPages: number;
  requests: ExtendedRequest[];
};

// Live update to a single already-loaded request, keyed by id. Lets the cache
// stay the source of truth so remounted list items don't revert to stale data.
type UpdateAction = {
  type: 'update';
  id: string;
  data: Partial<ExtendedRequest>;
};

type ResultAction = TSResult | UpdateAction | undefined;

const isUpdateAction = (action: ResultAction): action is UpdateAction =>
  !!action && 'type' in action && action.type === 'update';

const defaultResults = () => ({
  requests: [],
  hasMore: true,
  dedupeCheck: new Set<string>(),
  total: null, // until loaded
  filtered: null, // same
  incrementPage: () => {},
});

export const useSearchRequests = (input: SearchRequestArgs) => {
  const isLoading = useRef(false);
  const filterBy = buildFilter(input);
  const { typesenseClient } = useTypesense({}, true);
  const [results, dispatch] = useReducer(
    (state: ResultState, action: ResultAction) => {
      if (!action) return state;

      if (isUpdateAction(action)) {
        const requests = state.requests.map((req) =>
          req.id === action.id ? deepMergeObjects<ExtendedRequest>(req, action.data as ExtendedRequest) : req,
        );
        return { ...state, requests };
      }

      if (action.filteredCount === -1) {
        return defaultResults();
      }

      const filtered = action.requests.filter((req) => !state.dedupeCheck.has(req.id));
      filtered.map((req) => state.dedupeCheck.add(req.id));

      const requests = [...state.requests, ...filtered];

      let sorted: ExtendedRequest[];

      switch (input.sort.sort_order) {
        case 'created_at:desc':
          sorted = requests.toSorted((r1, r2) => parseInt(r2.id) - parseInt(r1.id));
          break;

        case 'created_at:asc':
          sorted = requests.toSorted((r1, r2) => parseInt(r1.id) - parseInt(r2.id));
          break;

        default:
          sorted = requests;
          break;
      }

      return {
        requests: sorted,
        hasMore: action.requests.length === input.take,
        dedupeCheck: state.dedupeCheck,
        total: action.totalCount,
        filtered: action.filteredCount,
      };
    },
    undefined,
    defaultResults,
  );

  const fetchAndUpdate = useCallback(
    async (page: number) => {
      if (!typesenseClient || isLoading.current) return;
      isLoading.current = true;
      const resPromise = typesenseClient
        .collections('requests')
        .documents()
        .search({
          q: input.search || '*',
          query_by: 'origins, destinations, author_email, formatted_id, awb_numbers',
          filter_by: filterBy,
          sort_by: input.sort.sort_order,
          per_page: input.take,
          page,
          include_fields: '$accounts(*) as account',
        })
        .finally(() => {
          isLoading.current = false;
        });
      const res = await resPromise;

      const requests = res.hits?.map((hit) => hit.document) as ExtendedRequest[];
      // Classifications only care about the first one
      const filteredRequests = requests?.filter((req) => {
        if (!input.filters?.request_classification) {
          return true;
        }
        return req.classifications?.[0]?.name === input.filters?.request_classification;
      });

      return {
        totalCount: res.out_of,
        filteredCount: res.found,
        totalPages: Math.ceil(res.found / input.take),
        requests: filteredRequests,
      };
    },
    [filterBy, input.sort, input.filters?.request_classification, input.search, input.take, typesenseClient],
  );

  const nextPage = useCallback(async () => {
    const fetched = await fetchAndUpdate(input.skip);
    dispatch(fetched);
    if (fetched) {
      input.incrementPage();
    }
  }, [fetchAndUpdate, input]);

  const fetchNew = useCallback(async () => {
    const fetched = await fetchAndUpdate(1);
    dispatch(fetched);
  }, [fetchAndUpdate]);

  const updateRequest = useCallback((id: string, data: Partial<ExtendedRequest>) => {
    dispatch({ type: 'update', id, data });
  }, []);

  trpc.onRequestTypesenseUpdate.useSubscription(undefined, {
    onData: (data) => {
      if (data.id > results.requests?.[0]?.id) {
        fetchNew();
      }
    },
  });

  const reset = () =>
    dispatch({
      filteredCount: -1,
      requests: [],
      totalCount: 0,
      totalPages: 0,
    });

  useEffect(() => {
    reset();
    fetchNew();
  }, [fetchNew, input.filters, input.search]);

  return { results, fetchNew, reset, nextPage, updateRequest };
};

export type SearchRequestResponse = ReturnType<typeof useSearchRequests>;
